/**
 * One marketplace card. State comes from the catalog entry's joined view
 * alone (`installed_version`, `latest_job`, `upgrade_available`): a card
 * shows `Installing…` only while its own job is in flight.
 *
 * A tool card is browse-only. Tools are installed inside every capability
 * that declares them, so the card has nothing to install or remove; it
 * shows the published version and links to the detail page. The infra
 * plugin's card installs and upgrades like any capability's, but has no
 * Uninstall: the platform keeps it installed (Feature 87, D4).
 *
 * Affordances on capability and policy cards:
 *   - Version picker (Install / Upgrade flows only) lets admins pin a
 *     non-latest version. Picking older while a different version is
 *     installed pops a Downgrade/Switch confirm.
 *   - Install / Upgrade / Retry — `POST /catalog/install`, then the
 *     grid's active-jobs poll picks up the job and the card flips to
 *     Installing automatically.
 *   - Uninstall — opens a confirmation dialog. Cascade is decided
 *     server-side; the success toast lists exactly which rows were
 *     removed.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { pluginId } from './plugin-id.js';
import { ApiError } from '@optum/api-client/client';
import { installCatalogEntry } from '@optum/api-client/endpoints/catalog';
import { uninstallPlugin } from '@optum/api-client/endpoints/plugins';
import type {
  CatalogEntryOut,
  CatalogKind,
  EnvironmentJobOut,
  InstallJobOut,
} from '@optum/api-client/types';
import { Button } from '../../ui/Button.js';
import { Spinner } from '../../ui/Spinner.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { Dialog, DialogActions } from '../../ui/Dialog.js';
import { Badge, Icon } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { CardPriority } from './CardPriority.js';
import { UninstallConfirmDialog } from './UninstallConfirmDialog.js';

type CardState =
  | { kind: 'not_installed' }
  | { kind: 'installed'; version: string }
  | { kind: 'upgrade'; installed: string; latest: string }
  | { kind: 'installing'; version: string }
  | { kind: 'rebuilding'; version: string }
  | { kind: 'failed'; version: string; error: string | null }
  | { kind: 'published'; version: string };

function deriveState(entry: CatalogEntryOut): CardState {
  const job: InstallJobOut | null = entry.latest_job;
  if (job && (job.state === 'pending' || job.state === 'running')) {
    return { kind: 'installing', version: job.version };
  }
  // An environment job rebuilds the installed version in place; while it runs
  // the card is busy the same way an install makes it busy.
  const environmentJob: EnvironmentJobOut | null = entry.latest_environment_job;
  if (
    environmentJob &&
    (environmentJob.state === 'pending' || environmentJob.state === 'running')
  ) {
    return { kind: 'rebuilding', version: environmentJob.plugin_version };
  }
  if (
    job &&
    job.state === 'failed' &&
    (!entry.installed_version || job.version !== entry.installed_version)
  ) {
    return { kind: 'failed', version: job.version, error: job.error };
  }
  if (entry.installed_version && entry.upgrade_available) {
    return {
      kind: 'upgrade',
      installed: entry.installed_version,
      latest: entry.latest_version,
    };
  }
  if (entry.installed_version) {
    return { kind: 'installed', version: entry.installed_version };
  }
  return { kind: 'not_installed' };
}

interface Props {
  kind: CatalogKind;
  entry: CatalogEntryOut;
}

export function MarketplaceCard({ kind, entry }: Props) {
  if (kind === 'tools') return <BrowseOnlyCard kind={kind} entry={entry} />;
  return <InstallableCard kind={kind} entry={entry} />;
}

/** The article every card shares: link, icon, name, version line, summary, badge. */
function CardShell({
  kind,
  failed,
  entry,
  badge,
  children,
}: {
  kind: CatalogKind;
  failed: boolean;
  entry: CatalogEntryOut;
  badge: ReactNode;
  children?: ReactNode;
}) {
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  const ref = { kind: 'plugin', id: pluginId(kind, entry.name) } as const;
  const open = () => panel.open(ref);
  const selected =
    panel.current?.kind === 'plugin' && panel.current.id === pluginId(kind, entry.name);
  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={entry.name}
      aria-pressed={selected}
      onClick={open}
      // The plugin page, as a row's double-click and Shift+Enter go to theirs
      // (Feature 87, D17, P4, P5).
      onDoubleClick={() => openPage(ref)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault();
          openPage(ref);
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
        padding: 16,
        textAlign: 'left',
      }}
    >
      <header style={{ display: 'flex', width: '100%', alignItems: 'flex-start', gap: 11 }}>
        <span
          style={{
            display: 'grid',
            width: 32,
            height: 32,
            flexShrink: 0,
            placeItems: 'center',
            borderRadius: 'var(--radius-input)',
            background: failed ? 'var(--danger-tint)' : 'var(--blue-tint)',
            color: failed ? 'var(--danger)' : 'var(--blue)',
          }}
        >
          <Icon name={failed ? 'warning' : 'extension'} size={16} />
        </span>
        <span style={{ minWidth: 0, flex: '1 1 0%' }}>
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'var(--text-body-md)',
              fontWeight: 'var(--weight-bold)',
              color: 'var(--text-strong)',
            }}
            className="mono"
          >
            {entry.name}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--text-muted)',
            }}
            className="mono"
          >
            v{entry.latest_version}
            {entry.versions.length > 1 && ` · ${entry.versions.length} versions`}
          </span>
        </span>
      </header>
      {/* Fixed minimum so a card with a one-line summary lines its footer up
          with the card beside it holding three. */}
      <p
        style={{
          minHeight: '34px',
          fontSize: 'var(--text-meta-lg)',
          lineHeight: 1.625,
          color: 'var(--text-faint)',
        }}
        className="clamp2"
      >
        {entry.summary ?? ''}
      </p>
      <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 7 }}>{badge}</div>
      {children}
    </article>
  );
}

/** A tool: published, browsable, never installed on its own. */
function BrowseOnlyCard({ kind, entry }: Props) {
  return (
    <CardShell
      kind={kind}
      failed={false}
      entry={entry}
      badge={<StateBadge state={{ kind: 'published', version: entry.latest_version }} />}
    />
  );
}

function InstallableCard({ kind, entry }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Non-yanked versions, newest first.
  const installableVersions = useMemo(
    () => entry.versions.filter((v) => !v.yanked),
    [entry.versions],
  );
  const [selectedVersion, setSelectedVersion] = useState<string>(entry.latest_version);
  // Keep the picker honest as the underlying entry refreshes.
  useMemo(() => {
    if (!installableVersions.some((v) => v.version === selectedVersion)) {
      setSelectedVersion(entry.latest_version);
    }
  }, [entry.latest_version, installableVersions, selectedVersion]);

  const [pendingVersion, setPendingVersion] = useState<string | null>(null);

  const install = useMutation({
    mutationFn: (version: string) => installCatalogEntry({ name: entry.name, version }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.installJobsActive() });
      qc.invalidateQueries({ queryKey: ['catalog', kind] });
    },
    onError: (err) =>
      toast.error('Install failed', err instanceof ApiError ? err.message : String(err)),
  });

  const uninstall = useMutation({
    mutationFn: (cascade: boolean) => uninstallPlugin({ name: entry.name, cascade }),
    onSuccess: (result) => {
      const removed = result.removed ?? [];
      const cascadedCount = Math.max(removed.length - 1, 0);
      const detail =
        cascadedCount > 0
          ? `Also removed ${cascadedCount} unreferenced dep${cascadedCount === 1 ? '' : 's'}: ${removed.slice(1).join(', ')}`
          : undefined;
      toast.success(`Uninstalled ${entry.name}`, detail);
      qc.invalidateQueries({ queryKey: ['catalog'] });
      qc.invalidateQueries({ queryKey: qk.plugins() });
      setConfirmOpen(false);
    },
    onError: (err) =>
      toast.error('Uninstall failed', err instanceof ApiError ? err.message : String(err)),
  });

  const state = useMemo(() => deriveState(entry), [entry]);
  const pending = install.isPending || uninstall.isPending;

  function startInstall(target: string) {
    const currentlyInstalled = entry.installed_version;
    if (currentlyInstalled && currentlyInstalled !== target) {
      // Switch / downgrade — confirm before firing.
      setPendingVersion(target);
      return;
    }
    install.mutate(target);
  }

  return (
    <CardShell
      kind={kind}
      failed={state.kind === 'failed'}
      entry={entry}
      badge={
        <>
          {entry.is_infra ? (
            <Badge tone="violet" shape="square">
              Infra
            </Badge>
          ) : null}
          <StateBadge state={state} />
          {entry.installed_status === 'disabled' ? (
            <Badge tone="warn" shape="square">
              disabled
            </Badge>
          ) : null}
          {entry.installed_version !== null && entry.capability_ids.length > 0 ? (
            <CardPriority capabilityIds={entry.capability_ids} />
          ) : null}
        </>
      }
    >
      {state.kind === 'failed' && state.error && (
        <p
          title={state.error}
          style={{
            borderRadius: 'var(--radius-chip)',
            border: '1px solid var(--danger-line)',
            background: 'var(--danger-tint)',
            padding: 7,
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--danger)',
          }}
        >
          {state.error.length > 200 ? state.error.slice(0, 197) + '…' : state.error}
        </p>
      )}
      <footer
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        style={{
          marginTop: 'auto',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 7,
          borderTop: '1px solid var(--border-hairline)',
          borderColor: 'var(--border-hairline)',
          paddingTop: 11,
        }}
      >
        {(state.kind === 'not_installed' || state.kind === 'upgrade' || state.kind === 'failed') &&
          installableVersions.length > 1 && (
            <VersionPicker
              versions={installableVersions.map((v) => v.version)}
              latestVersion={entry.latest_version}
              value={selectedVersion}
              onChange={setSelectedVersion}
              disabled={pending}
            />
          )}
        <ActionButtons
          state={state}
          pending={pending}
          onInstall={() => startInstall(selectedVersion)}
          // The infra plugin is installed by hand but never uninstalled
          // (Feature 87, D4): its card has no Uninstall.
          onUninstall={entry.is_infra ? undefined : () => setConfirmOpen(true)}
        />
      </footer>

      {/* Dialogs portal out; the `contents` wrapper stops their (React-tree)
          clicks and double-clicks from bubbling to the card's handlers. */}
      <div
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        style={{ display: 'contents' }}
      >
        <UninstallConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          name={entry.name}
          pending={uninstall.isPending}
          onConfirm={(cascade) => uninstall.mutate(cascade)}
        />

        <SwitchVersionDialog
          open={pendingVersion !== null}
          onOpenChange={(next) => {
            if (!next) setPendingVersion(null);
          }}
          name={entry.name}
          installed={entry.installed_version}
          target={pendingVersion}
          onConfirm={() => {
            if (pendingVersion) install.mutate(pendingVersion);
            setPendingVersion(null);
          }}
        />
      </div>
    </CardShell>
  );
}

function StateBadge({ state }: { state: CardState }) {
  const styles: Record<CardState['kind'], CSSProperties> = {
    not_installed: { background: 'var(--surface-fill)', color: 'var(--text-muted)' },
    installed: { background: 'var(--accent-tint)', color: 'var(--text-accent)' },
    upgrade: { background: 'var(--warn-tint)', color: 'var(--warn)' },
    installing: { background: 'var(--info-tint)', color: 'var(--info)' },
    rebuilding: { background: 'var(--info-tint)', color: 'var(--info)' },
    failed: { background: 'var(--danger-tint)', color: 'var(--danger)' },
    published: { background: 'var(--surface-fill)', color: 'var(--text-muted)' },
  };
  const labels: Record<CardState['kind'], string> = {
    not_installed: 'Not installed',
    installed: `Installed v${state.kind === 'installed' ? state.version : ''}`,
    upgrade: `Upgrade → v${state.kind === 'upgrade' ? state.latest : ''}`,
    installing: 'Installing',
    rebuilding: 'Rebuilding environment',
    failed: 'Failed',
    published: `Published v${state.kind === 'published' ? state.version : ''}`,
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        flexShrink: 0,
        alignItems: 'center',
        gap: 5,
        borderRadius: 'var(--radius-pill)',
        padding: '5px 7px',
        fontSize: 'var(--text-meta-lg)',
        fontWeight: 'var(--weight-medium)',
        ...styles[state.kind],
      }}
    >
      {(state.kind === 'installing' || state.kind === 'rebuilding') && <Spinner size={12} />}
      {labels[state.kind]}
    </span>
  );
}

function VersionPicker({
  versions,
  latestVersion,
  value,
  onChange,
  disabled,
}: {
  versions: string[];
  latestVersion: string;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 'var(--text-meta-lg)',
        color: 'var(--text-muted)',
      }}
    >
      <span className="sr-only">Version</span>
      <Select
        value={value}
        onChange={(value) => onChange(value)}
        disabled={disabled}
        options={[
          ...versions.map((v) => ({ value: v, label: v === latestVersion ? `${v} (latest)` : v })),
        ]}
      />
    </label>
  );
}

function ActionButtons({
  state,
  pending,
  onInstall,
  onUninstall,
}: {
  state: CardState;
  pending: boolean;
  onInstall: () => void;
  /** Absent for a plugin that is never uninstalled: no Uninstall is shown. */
  onUninstall: (() => void) | undefined;
}) {
  if (state.kind === 'installing') {
    return (
      <Button variant="primary" size="sm" disabled>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <Spinner />
          Installing
        </span>
      </Button>
    );
  }
  if (state.kind === 'rebuilding') {
    return (
      <>
        {onUninstall && (
          <Button size="sm" variant="ghost" disabled>
            Uninstall
          </Button>
        )}
        <Button variant="primary" size="sm" disabled>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Spinner />
            Rebuilding
          </span>
        </Button>
      </>
    );
  }
  if (state.kind === 'failed') {
    return (
      <>
        {onUninstall && (
          <Button size="sm" variant="ghost" onClick={onUninstall} disabled={pending}>
            Uninstall
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={onInstall} disabled={pending}>
          Retry
        </Button>
      </>
    );
  }
  if (state.kind === 'installed') {
    if (!onUninstall) return null;
    return (
      <Button size="sm" variant="danger" onClick={onUninstall} disabled={pending}>
        Uninstall
      </Button>
    );
  }
  if (state.kind === 'upgrade') {
    return (
      <>
        {onUninstall && (
          <Button size="sm" variant="ghost" onClick={onUninstall} disabled={pending}>
            Uninstall
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={onInstall} disabled={pending}>
          Upgrade
        </Button>
      </>
    );
  }
  return (
    <Button variant="primary" size="sm" onClick={onInstall} disabled={pending}>
      Install
    </Button>
  );
}

function SwitchVersionDialog({
  open,
  onOpenChange,
  name,
  installed,
  target,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  name: string;
  installed: string | null;
  target: string | null;
  onConfirm: () => void;
}) {
  if (!installed || !target) return null;
  const direction = compareVersions(target, installed) >= 0 ? 'Switch' : 'Downgrade';
  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      title={
        <>
          {direction} {name} to v{target}?
        </>
      }
      subtitle={
        <>
          Currently installed: v{installed}. The new version will replace it in
          <code className="mono"> plugins-site</code>.
        </>
      }
    >
      <DialogActions>
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button variant="primary" type="button" onClick={onConfirm}>
          {direction}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function compareVersions(a: string, b: string): number {
  // Lexicographic on dotted numeric parts; non-numeric segments fall
  // back to string comparison. Good enough for the
  // "newer or older than the installed?" copy on the confirm dialog.
  const pa = a.split('.');
  const pb = b.split('.');
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const xa = pa[i] ?? '0';
    const xb = pb[i] ?? '0';
    const na = Number(xa);
    const nb = Number(xb);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) {
      if (na !== nb) return na - nb;
    } else if (xa !== xb) {
      return xa < xb ? -1 : 1;
    }
  }
  return 0;
}
